/**
 * Account Service
 * Handles account-related API calls (super admin only)
 */

import api from '@/lib/api';
import { withBuScope } from '@/lib/business-unit-scope';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type { ApiResponse, AccountsListResponseDto } from '@/types';

export const accountService = {
  /**
   * Get account flags for the current authenticated user
   */
  async getAccountFlags(scope?: { bu_id?: string }) {
    const response = await api.get<ApiResponse<{ flags: Record<string, unknown> }>>(
      API_ENDPOINTS.ACCOUNTS.FLAGS,
      { params: scope }
    );
    return response.data.data;
  },

  /**
   * Get all accounts
   * Super admin only
   */
  async getAllAccounts(params?: { page?: number; limit?: number; search?: string; shadowMode?: 'all' | 'enabled' | 'disabled'; v3?: 'all' | 'enabled' | 'disabled' }) {
    const response = await api.get<ApiResponse<AccountsListResponseDto>>(
      API_ENDPOINTS.ACCOUNTS.BASE,
      { params }
    );
    return response.data.data;
  },

  /**
   * Update account flag
   * Super admin only
   */
  async updateAccountFlag(accountId: string, flagKey: string, flagValue: boolean | string | number) {
    const response = await api.patch<ApiResponse<{ flag: Record<string, unknown> }>>(
      API_ENDPOINTS.ACCOUNTS.FLAG(accountId),
      { flagKey, flagValue }
    );
    return response.data.data;
  },

  /**
   * Toggle intent automation kill switch on the caller's own account (JWT-scoped).
   */
  /**
   * `buScope` is not optional in practice. The backend reads this flag BRAND-FIRST, so a
   * write without a brand lands on the account while the settings page reads the brand's own
   * value — the switch saves, the toast confirms, and the toggle springs back because the
   * brand's frozen value is what gets read again.
   */
  async updateKillSwitch(enabled: boolean, buScope: { bu_id?: string } = {}) {
    const response = await api.patch<ApiResponse<{ flag: { shadow_mode_enabled: boolean; intent_automation_kill_switch: boolean } }>>(
      withBuScope(API_ENDPOINTS.ACCOUNTS.KILL_SWITCH, buScope),
      { enabled }
    );
    return response.data.data;
  },

  /**
   * Turn a brand-level flag (shadow mode, Kim AI) on or off for ONE brand (super admin only).
   *
   * `buScope` must name the brand: the backend refuses these writes without one, because readers
   * take the brand's own value first and an account-level write would not move it.
   */
  async updateBrandFlag(endpoint: string, enabled: boolean, buScope: { bu_id?: string }) {
    const response = await api.patch<ApiResponse<{ flag: Record<string, unknown> }>>(
      withBuScope(endpoint, buScope),
      { enabled }
    );
    return response.data.data;
  }
};
